import { useTranslation } from 'react-i18next';

type Stat = { value: string; label: string };
type Card = { title: string; description: string };

export default function About() {
  const { t } = useTranslation();
  const stats = t('about.stats', { returnObjects: true }) as Stat[];
  const cards = t('about.cards', { returnObjects: true }) as Card[];

  return (
    <section id="about" className="py-24 bg-gradient-to-br from-slate-900 via-slate-950 to-slate-900 relative overflow-hidden">
      {/* Glowing Orbs */}
      <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl"></div>
      <div className="absolute bottom-1/4 left-1/4 w-80 h-80 bg-teal-500/10 rounded-full blur-3xl"></div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left Content */}
          <div>
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-500/10 border border-cyan-500/20 rounded-full backdrop-blur-sm mb-6">
              <i className="ri-building-line text-cyan-400"></i>
              <span className="text-cyan-400 text-sm font-medium">{t('about.badge')}</span>
            </div>

            <h4 className="text-4xl lg:text-5xl font-black text-white mb-6 leading-tight">
              {t('about.title')}
            </h4>

            <p className="text-lg text-gray-400 leading-relaxed mb-6">
              {t('about.paragraph1')}
            </p>

            <p className="text-gray-400 leading-relaxed mb-8">
              {t('about.paragraph2')}
            </p>

            <div className="grid grid-cols-2 gap-6">
              {stats.map((stat, index) => (
                <div key={index} className="p-6 rounded-xl bg-slate-900/50 border border-cyan-500/10 backdrop-blur-sm">
                  <div className="text-3xl font-black bg-gradient-to-r from-cyan-400 to-teal-400 bg-clip-text text-transparent mb-2">
                    {stat.value}
                  </div>
                  <div className="text-sm text-gray-400">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Right Content - Tech Grid */}
          <div className="relative">
            <div className="grid grid-cols-2 gap-6">
              {cards.map((card, index) => {
                const isOffset = index % 2 === 1;
                const icons = [
                  'ri-rocket-line',
                  'ri-team-line',
                  'ri-shield-check-line',
                  'ri-line-chart-line',
                ];
                return (
                  <div
                    key={index}
                    className={`p-6 rounded-2xl bg-gradient-to-br from-slate-900/80 to-slate-900/40 border border-cyan-500/20 backdrop-blur-sm ${isOffset ? 'mt-12' : ''}`}
                  >
                    <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-cyan-500/20 border border-cyan-500/30 mb-4">
                      <i className={`${icons[index]} text-2xl text-cyan-400`}></i>
                    </div>
                    <h6 className="text-white font-bold mb-2">{card.title}</h6>
                    <p className="text-sm text-gray-400">{card.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
